import { Injectable } from '@angular/core';

import { BetBuilderView, OptionMarketsRequest } from '@cds/betting-offer/bet-builder';
import { MarketGroupConfig } from '@cds/betting-offer/grouping/bet-builder';
import { ExpiringCache, ExpiringCacheFactory } from '@frontend/sports/common/core/utils/expiring-cache';
import { LocalStoreService } from '@frontend/vanilla/core';
import { Observable, of, switchMap, tap } from 'rxjs';

import { BetBuilderApi } from '../../../cds/betbuilder-api.service';

@Injectable({
    providedIn: 'root',
})
export class MarketBuilderProvider {
    private cache: ExpiringCache;
    private cacheNull: any = { null: null };
    private readonly betBuilderGrouping = 'bet-builder-grouping';

    constructor(
        private betBuilderApi: BetBuilderApi,
        private expiringCacheFactory: ExpiringCacheFactory,
        private localStorage: LocalStoreService,
    ) {
        this.cache = this.expiringCacheFactory.create(600);
    }

    getMarketGroupConfig(sportId: number, groupingVersion?: string | 'any'): Observable<MarketGroupConfig | undefined> {
        const key = `${this.betBuilderGrouping}-${sportId}`;

        return this.cache.get<MarketGroupConfig>(key).pipe(
            switchMap((cacheConfiguration) => {
                if (this.isValidGroupingId(cacheConfiguration, groupingVersion)) {
                    return of(cacheConfiguration);
                }

                return this.getMarketGroupConfigFromApi(sportId, key, groupingVersion);
            }),
        );
    }

    getMarketList(request: OptionMarketsRequest): Observable<BetBuilderView | undefined> {
        return this.betBuilderApi.getSportcastMarkets(request);
    }

    private getMarketGroupConfigFromApi(sport: number, key: string, groupingVersion?: string) {
        return this.betBuilderApi.getMarketGroupConfig(sport).pipe(
            switchMap((grouping) => {
                if (this.isValidGroupingId(grouping, groupingVersion)) {
                    return this.cache.create(key, of(grouping));
                }

                return of(grouping);
            }),
            tap((grouping) => {
                if (this.isValidGroupingId(grouping, groupingVersion)) {
                    this.set(key, grouping);
                }
            }),
        );
    }

    private isValidGroupingId(groupingConfiguration: MarketGroupConfig | undefined, groupingVersion?: string): boolean {
        return !!(groupingConfiguration && (groupingVersion === 'any' || groupingVersion === groupingConfiguration.version));
    }

    private set<T>(key: string, value: T | undefined | null): void {
        this.localStorage.set(key, value || this.cacheNull);
    }
}
